/* Duskhaven product page enhancements (Sept 2026).
   1. Variant -> carousel sync: after a variant change Horizon swaps in a freshly rendered gallery;
      we point that gallery's first slide at the variant's own image (variant.featured_media) before
      it is inserted, so the carousel lands on it without a jump and the thumbnails never reshuffle.
   2. <dh-variant-preview>: mobile "your selection" card with the chosen variant's image.
   3. <dh-reel>: the slow rolling photo strip.
   Loaded as a module via snippets/dh-pdp-enhance-assets.liquid. */
import { StandardEvents } from '@shopify/events';

const MAIN = '.product-information';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @returns {HTMLElement | null} */
const mainGallery = () => document.querySelector(`${MAIN} media-gallery`);

/** Index of the gallery slide showing a media id, or -1. */
function slideIndexFor(mediaId, root = mainGallery()) {
  if (!root || mediaId == null) return -1;
  const slides = [...root.querySelectorAll('slideshow-slide')];
  return slides.findIndex((slide) => slide.getAttribute('slide-id') === String(mediaId));
}

/** Opens the gallery's full-screen zoom viewer on a media id; falls back to scrolling to the gallery. */
function openMedia(mediaId, event) {
  const gallery = mainGallery();
  if (!gallery) return;
  const index = slideIndexFor(mediaId, gallery);
  const zoom = gallery.querySelector('zoom-dialog');
  if (index > -1 && zoom && typeof gallery.zoom === 'function') {
    gallery.zoom(index, event);
    return;
  }
  showInGallery(mediaId);
}

/** Scrolls the gallery into view and selects the slide for a media id. */
function showInGallery(mediaId) {
  const gallery = mainGallery();
  if (!gallery) return;
  const index = slideIndexFor(mediaId, gallery);
  const slideshow = gallery.querySelector('slideshow-component');
  if (index > -1 && slideshow && typeof slideshow.select === 'function') {
    slideshow.select(index, undefined, { animate: false });
  }
  gallery.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
}

/* ---------- 1. Variant -> carousel sync ---------- */
/* Horizon's slideshow marks its initial slide as "current" before it has scrolled, then skips
   scrolling to it, so a non-zero initial-slide shows the first photo. Once the slideshow is on
   screen and ready, this nudges it to the wanted slide and checks for a few frames that it stuck. */
function showSlideWhenReady(slideshow, want) {
  const gallery = slideshow?.closest('media-gallery');
  const reveal = () => gallery?.classList.remove('dh-syncing');
  if (!slideshow || !(want > 0) || typeof slideshow.select !== 'function') return reveal();
  const safety = setTimeout(reveal, 1500);
  const run = () => {
    let frames = 0;
    let stable = 0;
    const done = () => {
      clearTimeout(safety);
      reveal();
    };
    const tick = () => {
      if (!slideshow.isConnected) return;
      const slides = [...slideshow.querySelectorAll('slideshow-slide')];
      const scroller = slideshow.querySelector('slideshow-slides');
      const target = slides[want];
      if (!target || !scroller) return done();
      const left = scroller.getBoundingClientRect().left;
      const near = (el) => Math.abs(el.getBoundingClientRect().left - left) < 8;
      if (near(target)) {
        reveal();
        if (++stable > 8) return done();
      } else {
        stable = 0;
        const shown = slides.findIndex(near);
        slideshow.current = shown < 0 ? 0 : shown;
        slideshow.select(want, undefined, { animate: false });
      }
      if (++frames < 120) requestAnimationFrame(tick);
      else done();
    };
    requestAnimationFrame(tick);
  };
  if (!('IntersectionObserver' in window)) return run();
  const io = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return reveal();
    io.disconnect();
    run();
  });
  io.observe(slideshow);
}

const mainSlideshow = () => mainGallery()?.querySelector('slideshow-component');
const initialOf = (slideshow) => parseInt(slideshow?.getAttribute('initial-slide') || '0', 10) || 0;

// Page opened with ?variant=: the gallery is rendered with that variant's photo as initial slide.
const onReady = () => showSlideWhenReady(mainSlideshow(), initialOf(mainSlideshow()));
document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', onReady) : onReady();

// Capture phase on document runs before media-gallery's own listener, so our promise callback
// runs first and can edit the incoming gallery HTML before media-gallery inserts it.
document.addEventListener(
  StandardEvents.productSelect,
  (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest(MAIN)) return;
    if (target.closest('product-card, quick-add-component, dialog')) return;

    const current = mainGallery()?.querySelector('slideshow-component');
    const currentIndex = Number(current?.current ?? 0) || 0;

    event.promise
      ?.then((result) => {
        const detail = result?.detail;
        const html = detail?.html;
        if (!html) return;
        const incoming = html.querySelector('media-gallery slideshow-component');
        if (!incoming) return;

        const mediaId = detail.resource?.featured_media?.id;
        const slides = [...incoming.querySelectorAll('slideshow-slide')];
        let index = mediaId != null ? slides.findIndex((s) => s.getAttribute('slide-id') === String(mediaId)) : -1;
        if (index < 0) index = Math.min(currentIndex, Math.max(slides.length - 1, 0));
        incoming.setAttribute('initial-slide', String(index));
        // Hide the brief flash of the first photo while the slideshow moves to the variant's photo.
        if (index > 0) incoming.closest('media-gallery')?.classList.add('dh-syncing');
        // media-gallery swaps the new gallery in right after this callback.
        requestAnimationFrame(() => showSlideWhenReady(mainSlideshow(), index));
      })
      .catch(() => {});
  },
  { capture: true }
);

/* ---------- 2. Selection preview (mobile) ---------- */
class DhVariantPreview extends HTMLElement {
  #variants = {};
  #io;
  #pinIo;
  #stuck = false;
  #inRange = false;

  connectedCallback() {
    try {
      this.#variants = JSON.parse(this.querySelector('[data-dh-variants]')?.textContent || '{}');
    } catch {
      this.#variants = {};
    }
    this.img = this.querySelector('[data-dh-img]');
    this.titleEl = this.querySelector('[data-dh-title]');
    this.media = this.querySelector('[data-dh-zoom]');

    this.media?.addEventListener('click', (event) => openMedia(this.media.dataset.mediaId, event));
    this.querySelector('[data-dh-view]')?.addEventListener('click', () => showInGallery(this.media?.dataset.mediaId));

    this.onChange = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLElement) || !input.closest(`${MAIN} variant-picker`)) return;
      const id = input instanceof HTMLSelectElement ? input.selectedOptions[0]?.dataset.variantId : input.dataset.variantId;
      if (id) this.show(id);
    };
    this.onSelect = (event) => {
      if (!(event.target instanceof Element) || !event.target.closest(MAIN)) return;
      event.promise
        ?.then((result) => {
          const id = result?.detail?.resource?.id;
          if (id) this.show(String(id), false);
        })
        .catch(() => {});
    };
    document.addEventListener('change', this.onChange);
    document.addEventListener(StandardEvents.productSelect, this.onSelect);
    this.#observe();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onChange);
    document.removeEventListener(StandardEvents.productSelect, this.onSelect);
    this.#io?.disconnect();
    this.#pinIo?.disconnect();
  }

  /** @param {string} id @param {boolean} [flash] */
  show(id, flash = true) {
    const data = this.#variants[id];
    if (!data) return;
    if (this.titleEl && data.title) this.titleEl.textContent = data.title;
    if (!this.img || !data.src) return;
    const changed = this.media?.dataset.mediaId !== String(data.media);
    if (this.media) this.media.dataset.mediaId = String(data.media);
    if (!changed) return;
    const next = new Image();
    next.src = data.src;
    const swap = () => {
      this.img.removeAttribute('srcset');
      this.img.src = data.src;
      if (flash && !reducedMotion.matches) {
        this.classList.remove('is-changed');
        void this.offsetWidth;
        this.classList.add('is-changed');
      }
    };
    next.decode ? next.decode().then(swap, swap) : swap();
  }

  /* The card is always visible in its own spot. Once it pins to the top it only stays
     visible while the options or buy buttons are on screen. */
  #observe() {
    // Whatever sits right above the card (the divider) tells us when the card has pinned.
    let sentinel = this.previousElementSibling;
    while (sentinel && /^(SCRIPT|LINK|STYLE)$/.test(sentinel.tagName)) sentinel = sentinel.previousElementSibling;
    sentinel = sentinel || this.parentElement;
    const root = this.closest('.product-details') || document;
    const watched = [
      root.querySelector('variant-picker'),
      root.querySelector('.buy-buttons-block, product-form-component'),
    ].filter(Boolean);
    if (!sentinel || !('IntersectionObserver' in window)) return;

    const visible = new Set();
    const update = () => {
      this.classList.toggle('is-stuck', this.#stuck);
      this.classList.toggle('is-away', this.#stuck && !this.#inRange);
    };
    // Pinned once the sentinel (the card's natural top) has scrolled above the pin line.
    this.#pinIo = new IntersectionObserver(
      ([entry]) => {
        this.#stuck = !entry.isIntersecting && entry.boundingClientRect.bottom < 20;
        update();
      },
      { rootMargin: '-12px 0px 0px 0px' }
    );
    this.#pinIo.observe(sentinel);
    // "In range" while the options or buy buttons are in the middle band of the screen.
    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target);
        }
        this.#inRange = visible.size > 0;
        update();
      },
      { rootMargin: '-120px 0px -30% 0px' }
    );
    watched.forEach((el) => this.#io.observe(el));
  }
}
if (!customElements.get('dh-variant-preview')) customElements.define('dh-variant-preview', DhVariantPreview);

/* ---------- 3. Rolling reel ----------
   Oct 2026 (Todd): the strip keeps rolling at all times. Only the Pause/Play button stops or
   starts it - hovering, touching, keyboard focus or scrolling it off screen no longer pause it.
   Visitors whose device asks for reduced motion start on Pause and can press Play. */
class DhReel extends HTMLElement {
  #paused = false;
  #controller;

  connectedCallback() {
    this.track = this.querySelector('[data-dh-reel-track]');
    this.toggleBtn = this.querySelector('[data-dh-reel-toggle]');
    if (!this.track) return;

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#setDuration();
    this.ro?.disconnect();
    this.ro = new ResizeObserver(() => this.#setDuration());
    this.ro.observe(this.track);

    this.#setPaused(reducedMotion.matches);
    this.toggleBtn?.addEventListener('click', () => this.#setPaused(!this.#paused), { signal });

    this.track.addEventListener(
      'click',
      (event) => {
        const tile = event.target instanceof Element ? event.target.closest('button[data-media-id]') : null;
        if (tile) openMedia(tile.getAttribute('data-media-id'), event);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.ro?.disconnect();
  }

  /** @param {boolean} paused */
  #setPaused(paused) {
    this.#paused = paused;
    this.classList.toggle('is-paused', paused);
    if (!this.toggleBtn) return;
    this.toggleBtn.setAttribute('aria-pressed', String(paused));
    const text = this.toggleBtn.querySelector('.dh-reel__toggle-text');
    if (text) text.textContent = paused ? text.dataset.labelPlay : text.dataset.labelPause;
  }

  #setDuration() {
    const lap = this.track.scrollWidth / 2;
    const speed = Number(this.dataset.speed) || 36;
    if (lap > 0) this.style.setProperty('--dh-reel-duration', `${Math.max(20, Math.round(lap / speed))}s`);
  }
}
if (!customElements.get('dh-reel')) customElements.define('dh-reel', DhReel);
